<template>
    <VModalWrapper
        :name="modalName"
        title="Поиск везде"
        width="680"
        height="auto"
        @before-open="onOpen"
        @before-close="onClose"
    >
        <div class="global-search">
            <form class="global-search__form" @submit.prevent="submit">
                <input
                    ref="query"
                    v-model="query"
                    class="global-search__input"
                    type="search"
                    maxlength="160"
                    placeholder="Номер обращения или описание, категория, сервис"
                    aria-label="Глобальный поиск"
                />
                <button type="submit" :disabled="!query.trim()">Найти</button>
            </form>
            <p v-if="loading" role="status">Ищем…</p>
            <div v-if="error" class="global-search__error" role="alert">
                {{ error }} <button type="button" @click="submit">Повторить</button>
            </div>

            <template v-if="!query.trim() && !loading">
                <div class="global-search__section-title">История поиска</div>
                <p v-if="!history.length && !error">История пока пуста.</p>
                <div v-for="item in history" :key="item.id" class="global-search__history-row">
                    <button type="button" @click="runFromHistory(item.query)">{{ item.query }}</button>
                    <button type="button" :aria-label="`Удалить запрос ${item.query}`" @click="removeHistory(item.id)">
                        ×
                    </button>
                </div>
                <button v-if="history.length" type="button" class="global-search__clear" @click="clearHistory">
                    Очистить историю
                </button>
            </template>

            <template v-else-if="searched && !loading && !error">
                <div class="global-search__section-title">Обращения</div>
                <button
                    v-for="item in results.requests"
                    :key="`r-${item.id}`"
                    type="button"
                    class="global-search__result"
                    @click="openRequest(item)"
                >
                    <strong>№{{ item.id }}</strong>
                    <span v-if="item.archive" class="global-search__badge">Архив</span>
                    <span>Статус: {{ statusTitle(item.status) }}</span>
                    <span v-if="item.category || item.service">{{ item.category }} · {{ item.service }}</span>
                    <span v-if="item.description" class="global-search__description">{{ item.description }}</span>
                </button>
                <p v-if="!results.requests.length">Обращений не найдено.</p>

                <div class="global-search__section-title">Категории и сервисы</div>
                <button
                    v-for="item in results.catalog"
                    :key="`s-${item.serviceId}`"
                    type="button"
                    class="global-search__result"
                    @click="openService(item)"
                >
                    <strong>{{ item.service }}</strong
                    ><span>{{ item.category }}</span>
                </button>
                <p v-if="!results.catalog.length">Категорий и сервисов не найдено.</p>
            </template>
        </div>
    </VModalWrapper>
</template>
<script>
import http from '@/js/http';
import ModalNames from '@/js/enums/ModalNames';
import RequestStatusTitle from '@/js/enums/RequestStatusTitle';
import VModalWrapper from '@/views/components/VModalWrapper/VModalWrapper.vue';

export default {
    components: { VModalWrapper },
    data() {
        return {
            modalName: ModalNames.GLOBAL_SEARCH,
            query: '',
            history: [],
            results: { requests: [], catalog: [] },
            searched: false,
            loading: false,
            error: '',
            sequence: 0,
            isOpen: false,
        };
    },
    methods: {
        statusTitle(status) {
            return RequestStatusTitle[status] || status;
        },
        onOpen() {
            this.isOpen = true;
            this.query = '';
            this.results = { requests: [], catalog: [] };
            this.searched = false;
            this.error = '';
            this.loading = false;
            this.sequence++;
            document.addEventListener('keydown', this.onKeydown);
            this.loadHistory();
            this.$nextTick(() => this.$refs.query?.focus());
        },
        onClose() {
            this.isOpen = false;
            this.sequence++;
            document.removeEventListener('keydown', this.onKeydown);
        },
        onKeydown(event) {
            if (event.key === 'Escape') this.$modal.hide(this.modalName);
        },
        async loadHistory() {
            const sequence = this.sequence;
            try {
                const { data } = await http.get('/global-search/history');
                if (this.isOpen && sequence === this.sequence) this.history = data;
            } catch (error) {
                if (this.isOpen && sequence === this.sequence) this.error = 'Не удалось загрузить историю.';
            }
        },
        runFromHistory(query) {
            this.query = query;
            this.submit();
        },
        async submit() {
            const query = this.query.trim();
            if (!query) return;
            const sequence = ++this.sequence;
            this.loading = true;
            this.error = '';
            this.searched = false;
            this.results = { requests: [], catalog: [] };
            try {
                const { data } = await http.post('/global-search', { query });
                if (!this.isOpen || sequence !== this.sequence || this.query.trim() !== query) return;
                this.results = data;
                this.searched = true;
                this.loadHistory();
            } catch (error) {
                if (!this.isOpen || sequence !== this.sequence || this.query.trim() !== query) return;
                this.error = error.response?.data?.message || 'Поиск не удался.';
            } finally {
                if (sequence === this.sequence) this.loading = false;
            }
        },
        async removeHistory(id) {
            try {
                await http.delete(`/global-search/history/${id}`);
                this.history = this.history.filter((item) => item.id !== id);
            } catch (error) {
                this.error = 'Не удалось удалить запрос.';
            }
        },
        async clearHistory() {
            try {
                await http.delete('/global-search/history');
                this.history = [];
            } catch (error) {
                this.error = 'Не удалось очистить историю.';
            }
        },
        openRequest(item) {
            this.$modal.hide(this.modalName);
            this.$router.push({ name: 'request', params: { method: 'store', id: item.id } });
        },
        openService(item) {
            this.$modal.hide(this.modalName);
            this.$showModal(ModalNames.REQUEST_CREATE, { categoryId: item.categoryId, serviceId: item.serviceId })
                .then((created) => this.$router.push({ name: 'request', params: { method: 'outbox', id: created.id } }))
                .catch(() => {});
        },
    },
    beforeDestroy() {
        document.removeEventListener('keydown', this.onKeydown);
    },
};
</script>
<style lang="scss" scoped>
.global-search {
    max-height: 70vh;
    overflow-y: auto;
    min-width: 0;

    &__form {
        display: flex;
        gap: 8px;
    }
    &__input {
        flex: 1;
        min-width: 0;
        padding: 10px;
        border: 1px solid #d4d7db;
        border-radius: 6px;
    }
    &__form button {
        padding: 0 16px;
    }
    &__section-title {
        margin: 20px 0 8px;
        font-weight: 600;
    }
    &__result {
        display: flex;
        flex-wrap: wrap;
        gap: 6px 10px;
        width: 100%;
        padding: 10px;
        text-align: left;
        background: white;
        border: 1px solid #e3e5e8;
        border-radius: 6px;
        margin-bottom: 6px;
        cursor: pointer;
    }
    &__description {
        flex-basis: 100%;
        overflow-wrap: anywhere;
    }
    &__badge {
        color: #546e7a;
    }
    &__history-row {
        display: flex;
        justify-content: space-between;
        gap: 8px;
    }
    &__history-row button:first-child {
        flex: 1;
        text-align: left;
    }
    &__clear {
        margin-top: 12px;
    }
    &__error {
        margin-top: 12px;
        color: #b00020;
    }
}
@media (max-width: 600px) {
    .global-search__form {
        flex-wrap: wrap;
    }
    .global-search__form button {
        min-height: 38px;
    }
}
</style>
